import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { API_URL, getErrorMessage } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import { useConfirm } from '../components/ConfirmProvider';
import StateBlock, { Spinner } from '../components/StateBlock';
import { formatCurrency } from '../utils/format';
import { getListaStats } from '../utils/lista';

const upsertLista = (listas, lista) =>
  listas.some((current) => current._id === lista._id)
    ? listas.map((current) => (current._id === lista._id ? { ...current, ...lista } : current))
    : [...listas, lista];

const MinhasListas = () => {
  const { authData, isAuthenticated } = useAuth();
  const { userId } = authData;
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [nomeLista, setNomeLista] = useState('');
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState({ id: null, nome: '' });
  const [sharing, setSharing] = useState({ id: null, email: '' });

  const fetchListas = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }

      try {
        const { data } = await api.get('/listas');
        setListas(Array.isArray(data) ? data : []);
        setLoadError('');
      } catch (error) {
        setLoadError(getErrorMessage(error, 'Não foi possível carregar suas listas.'));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isAuthenticated) {
      fetchListas();
    }
  }, [fetchListas, isAuthenticated]);

  // Mensagens vindas de outra página (ex.: lista removida enquanto aberta).
  useEffect(() => {
    const feedback = location.state?.feedback;

    if (!feedback) {
      return;
    }

    toast[feedback.type === 'error' ? 'error' : 'info'](feedback.text);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, toast]);

  // Sincroniza entre as abas/aparelhos do próprio usuário.
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      return undefined;
    }

    const socket = io(API_URL || undefined, { transports: ['websocket', 'polling'] });
    const room = `user_${userId}`;
    let hasJoined = false;

    socket.on('connect', () => {
      socket.emit('join', room);

      if (hasJoined) {
        fetchListas({ silent: true });
      }

      hasJoined = true;
    });

    socket.on('lista_nova', (lista) => setListas((prev) => upsertLista(prev, lista)));
    socket.on('lista_atualizada', (lista) => setListas((prev) => upsertLista(prev, lista)));
    socket.on('lista_removida', ({ id }) =>
      setListas((prev) => prev.filter((lista) => lista._id !== id))
    );

    return () => {
      socket.emit('leave', room);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [fetchListas, isAuthenticated, userId]);

  const isOwner = useCallback(
    (lista) => {
      const ownerId =
        typeof lista.usuarioId === 'string' ? lista.usuarioId : lista.usuarioId?._id;

      return ownerId === userId;
    },
    [userId]
  );

  const criarLista = async (event) => {
    event.preventDefault();
    const nome = nomeLista.trim();

    if (!nome) {
      toast.error('Dê um nome para a lista.');
      return;
    }

    setIsCreating(true);

    try {
      const { data } = await api.post('/listas', { nome });
      setListas((prev) => upsertLista(prev, data));
      setNomeLista('');
      toast.success(`Lista "${data.nome}" criada.`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível criar a lista.'));
    } finally {
      setIsCreating(false);
    }
  };

  const renomearLista = async (event, id) => {
    event.preventDefault();
    const nome = editing.nome.trim();

    if (!nome) {
      toast.error('O nome da lista não pode ficar vazio.');
      return;
    }

    try {
      const { data } = await api.put(`/listas/${id}`, { nome });
      setListas((prev) => upsertLista(prev, data));
      setEditing({ id: null, nome: '' });
      toast.success('Nome atualizado.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível renomear a lista.'));
    }
  };

  const excluirLista = async (lista) => {
    const owner = isOwner(lista);
    const confirmed = await confirm({
      title: `Excluir "${lista.nome}"?`,
      message: owner
        ? 'Todos os itens e categorias serão removidos definitivamente.'
        : 'Esta lista foi compartilhada com você: excluir remove a lista para todos os participantes.',
      confirmLabel: 'Excluir lista',
      tone: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/listas/${lista._id}`);
      setListas((prev) => prev.filter((current) => current._id !== lista._id));
      toast.success('Lista removida.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível excluir a lista.'));
    }
  };

  const compartilhar = async (event, id) => {
    event.preventDefault();
    const email = sharing.email.trim();

    if (!email) {
      toast.error('Informe o e-mail de quem vai receber a lista.');
      return;
    }

    try {
      await api.post(`/listas/${id}/share`, { email });
      setSharing({ id: null, email: '' });
      toast.success('Lista compartilhada com sucesso.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível compartilhar a lista.'));
    }
  };

  const resumo = useMemo(() => {
    const totals = listas.reduce(
      (acc, lista) => {
        const stats = getListaStats(lista);

        return {
          items: acc.items + stats.totalItems,
          completed: acc.completed + stats.completedItems,
          value: acc.value + stats.totalValue,
          shared: acc.shared + (isOwner(lista) ? 0 : 1),
        };
      },
      { items: 0, completed: 0, value: 0, shared: 0 }
    );

    return totals;
  }, [listas, isOwner]);

  const listasFiltradas = useMemo(() => {
    const term = search.trim().toLowerCase();

    return term
      ? listas.filter((lista) => (lista.nome || '').toLowerCase().includes(term))
      : listas;
  }, [listas, search]);

  return (
    <div className="page">
      <section className="surface-card card-pad page-header">
        <div className="page-header__text">
          <span className="eyebrow">Painel</span>
          <h1 className="page-title">Minhas listas</h1>
          <p className="section-copy">
            Crie listas, acompanhe totais e compartilhe as compras da casa com quem
            faz mercado com você.
          </p>
        </div>

        <div className="metric-grid">
          <div className="metric">
            <span className="metric__label">Listas</span>
            <strong className="metric__value">{listas.length}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Itens</span>
            <strong className="metric__value">{resumo.items}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Compartilhadas</span>
            <strong className="metric__value">{resumo.shared}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Valor somado</span>
            <strong className="metric__value">{formatCurrency(resumo.value)}</strong>
          </div>
        </div>
      </section>

      <section className="surface-card card-pad list-toolbar" aria-label="Criar e filtrar listas">
        <form className="field" onSubmit={criarLista}>
          <label className="field-label" htmlFor="nova-lista">
            Nova lista
          </label>
          <div className="inline-form">
            <input
              id="nova-lista"
              type="text"
              className="input"
              placeholder="Ex.: Compras do fim de semana"
              value={nomeLista}
              enterKeyHint="done"
              onChange={(event) => setNomeLista(event.target.value)}
            />
            <button type="submit" className="btn btn--primary" disabled={isCreating}>
              {isCreating ? <Spinner label="Criando..." /> : 'Criar lista'}
            </button>
          </div>
        </form>

        <div className="field">
          <label className="field-label" htmlFor="busca-lista">
            Buscar
          </label>
          <input
            id="busca-lista"
            type="search"
            className="input"
            placeholder="Filtrar por nome"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </section>

      {loading && <StateBlock busy title="Carregando suas listas..." />}

      {!loading && loadError && (
        <StateBlock icon="bi-wifi-off" title="Não foi possível carregar" description={loadError}>
          <button type="button" className="btn btn--primary" onClick={() => fetchListas()}>
            Tentar novamente
          </button>
        </StateBlock>
      )}

      {!loading && !loadError && listas.length === 0 && (
        <StateBlock
          icon="bi-journal-plus"
          title="Você ainda não tem listas"
          description="Crie a primeira lista acima para começar a organizar suas compras."
        />
      )}

      {!loading && !loadError && listas.length > 0 && listasFiltradas.length === 0 && (
        <StateBlock
          icon="bi-search"
          title="Nenhuma lista encontrada"
          description={`Nada corresponde a "${search}".`}
        />
      )}

      {listasFiltradas.length > 0 && (
        <div className="card-grid">
          {listasFiltradas.map((lista) => {
            const stats = getListaStats(lista);
            const owner = isOwner(lista);

            return (
              <article className="surface-card card-pad lista-card" key={lista._id}>
                <div className="lista-card__top">
                  <span className={`badge ${owner ? 'badge--primary' : 'badge--accent'}`}>
                    <i
                      className={`bi ${owner ? 'bi-person-fill' : 'bi-people-fill'}`}
                      aria-hidden="true"
                    ></i>
                    {owner ? 'Sua lista' : 'Compartilhada'}
                  </span>
                  <div className="lista-card__total">
                    <span>Total</span>
                    <strong>{formatCurrency(stats.totalValue)}</strong>
                  </div>
                </div>

                <h2 className="lista-card__title">
                  <Link to={`/lista/${lista._id}`}>{lista.nome}</Link>
                </h2>

                <div className="lista-card__progress">
                  <div className="lista-card__progress-label">
                    <span>
                      {stats.completedItems} de {stats.totalItems} itens
                    </span>
                    <span>{stats.progress}%</span>
                  </div>
                  <div
                    className="progress"
                    role="progressbar"
                    aria-valuenow={stats.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Progresso de ${lista.nome}`}
                  >
                    <div className="progress__bar" style={{ width: `${stats.progress}%` }}></div>
                  </div>
                </div>

                <div className="btn-row">
                  <Link to={`/lista/${lista._id}`} className="btn btn--primary btn--sm">
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true"></i>
                    Abrir
                  </Link>
                  {owner && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--icon-sm"
                      onClick={() => {
                        setSharing({ id: lista._id, email: '' });
                        setEditing({ id: null, nome: '' });
                      }}
                      aria-label={`Compartilhar ${lista.nome}`}
                      title="Compartilhar"
                    >
                      <i className="bi bi-person-plus" aria-hidden="true"></i>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon-sm"
                    onClick={() => {
                      setEditing({ id: lista._id, nome: lista.nome });
                      setSharing({ id: null, email: '' });
                    }}
                    aria-label={`Renomear ${lista.nome}`}
                    title="Renomear"
                  >
                    <i className="bi bi-pencil" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon-sm"
                    onClick={() => excluirLista(lista)}
                    aria-label={`Excluir ${lista.nome}`}
                    title="Excluir"
                  >
                    <i className="bi bi-trash3" aria-hidden="true"></i>
                  </button>
                </div>

                {editing.id === lista._id && (
                  <form className="inline-panel" onSubmit={(event) => renomearLista(event, lista._id)}>
                    <label className="field">
                      <span className="field-label">Novo nome</span>
                      <input
                        type="text"
                        className="input"
                        value={editing.nome}
                        autoFocus
                        onChange={(event) =>
                          setEditing((prev) => ({ ...prev, nome: event.target.value }))
                        }
                      />
                    </label>
                    <div className="btn-row btn-row--end">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing({ id: null, nome: '' })}
                      >
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn--primary btn--sm">
                        Salvar
                      </button>
                    </div>
                  </form>
                )}

                {sharing.id === lista._id && (
                  <form className="inline-panel" onSubmit={(event) => compartilhar(event, lista._id)}>
                    <label className="field">
                      <span className="field-label">E-mail de quem vai receber</span>
                      <input
                        type="email"
                        className="input"
                        placeholder="pessoa@email.com"
                        value={sharing.email}
                        autoFocus
                        onChange={(event) =>
                          setSharing((prev) => ({ ...prev, email: event.target.value }))
                        }
                      />
                      <span className="field-hint">
                        A pessoa precisa já ter uma conta no aplicativo.
                      </span>
                    </label>
                    <div className="btn-row btn-row--end">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setSharing({ id: null, email: '' })}
                      >
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn--primary btn--sm">
                        Compartilhar
                      </button>
                    </div>
                  </form>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MinhasListas;
